import React, { useState, useEffect } from 'react';
import {
  Card, IconButton, CircularProgress, Box, Chip, Typography,
} from '@mui/material';
import {
  Add, Edit, Delete, Visibility, QuizOutlined,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import { useServicePlanApi } from '../services/servicePlanApi';
import { Notify } from 'notiflix';
import CreateQuestionnaireModal from './CreateQuestionnaireModal';
import ViewQuestionnaireModal from './ViewQuestionnaireModal';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const ACCENT  = '#667eea';
const ACCENT2 = '#764ba2';

// ── Column definitions
const columns = [
  { Header: '#',         accessor: 'sno',       width: '5%',  align: 'left'   },
  { Header: 'Name',      accessor: 'name',                     align: 'left'   },
  { Header: 'Page',      accessor: 'page',                     align: 'left'   },
  { Header: 'Questions', accessor: 'questions',  width: '10%', align: 'center' },
  { Header: 'Status',    accessor: 'status',    width: '10%', align: 'center' },
  { Header: 'Actions',   accessor: 'actions',   width: '12%', align: 'center' },
];

function QuestionnairesPage() {
  const canRead = usePermission('qa-center', 'read');
  const canCreate = usePermission('qa-center', 'create');
  const { questionnairesApi } = useServicePlanApi();

  const [questionnaires,       setQuestionnaires]       = useState([]);
  const [loading,              setLoading]              = useState(false);
  const [createModalOpen,      setCreateModalOpen]      = useState(false);
  const [editModalOpen,        setEditModalOpen]        = useState(false);
  const [viewModalOpen,        setViewModalOpen]        = useState(false);
  const [selectedQuestionnaire,setSelectedQuestionnaire]= useState(null);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    fetchQuestionnaires();
  }, [canRead]);

  const fetchQuestionnaires = async () => {
    setLoading(true);
    try {
      const response = await questionnairesApi.getAll();
      setQuestionnaires(response.data.data || response.data);
    } catch {
      Notify.failure('Failed to load questionnaires');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete questionnaire "${name}"?\nAll linked questions will become orphaned.`)) return;
    try {
      await questionnairesApi.delete(id, name);
      Notify.success('Questionnaire deleted');
      fetchQuestionnaires();
    } catch (e) {
      Notify.failure(e.response?.data?.message || 'Failed to delete questionnaire');
    }
  };

  const handleEdit = q => { setSelectedQuestionnaire(q); setEditModalOpen(true); };
  const handleView = q => { setSelectedQuestionnaire(q); setViewModalOpen(true); };

  const handleCloseModals = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setViewModalOpen(false);
    setSelectedQuestionnaire(null);
  };

  const handleSave = () => { handleCloseModals(); fetchQuestionnaires(); };

  // ── Row builder
  const getRow = (q, idx) => ({
    sno: (
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%',
        background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
          {idx + 1}
        </Typography>
      </Box>
    ),

    name: (
      <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
          {q.name}
        </Typography>
        {q.description && (
          <Typography sx={{ fontSize: 13, color: '#6b7280', display: 'block', mt: 0.3, lineHeight: 1.4 }}>
            {q.description.length > 60 ? q.description.slice(0, 60) + '…' : q.description}
          </Typography>
        )}
      </Box>
    ),

    page: (
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>
          {q.page?.name || '—'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', mt: 0.2, lineHeight: 1.3 }}>
          {q.page?.route || ''}
        </Typography>
      </Box>
    ),

    questions: (
      <Chip
        label={q.questionCount || 0}
        size="small"
        sx={{
          height: 26, fontSize: 13, fontWeight: 700,
          bgcolor: (q.questionCount > 0) ? `${ACCENT}15` : 'rgba(0,0,0,0.06)',
          color:   (q.questionCount > 0) ? ACCENT        : '#6b7280',
          border: '1px solid',
          borderColor: (q.questionCount > 0) ? `${ACCENT}35` : 'transparent',
          '& .MuiChip-label': { px: 1.2 },
        }}
      />
    ),

    status: (
      <Chip
        label={q.isActive ? 'Active' : 'Inactive'}
        size="small"
        sx={{
          height: 26, fontSize: 12, fontWeight: 700,
          bgcolor: q.isActive ? 'rgba(22,163,74,0.1)'   : 'rgba(107,114,128,0.1)',
          color:   q.isActive ? '#16a34a'                : '#6b7280',
          border: '1px solid',
          borderColor: q.isActive ? 'rgba(22,163,74,0.3)' : 'rgba(107,114,128,0.25)',
          '& .MuiChip-label': { px: 1.2 },
        }}
      />
    ),

    actions: (
      <MDBox display="flex" gap={0.5} justifyContent="center">
        <IconButton
          size="small"
          onClick={() => handleView(q)}
          sx={{ color: ACCENT, '&:hover': { bgcolor: `${ACCENT}15` } }}
          title="View details"
        >
          <Visibility fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleEdit(q)}
          sx={{ color: '#3b82f6', '&:hover': { bgcolor: 'rgba(59,130,246,0.12)' } }}
          title="Edit"
        >
          <Edit fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleDelete(q.id, q.name)}
          sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}
          title="Delete"
        >
          <Delete fontSize="small" />
        </IconButton>
      </MDBox>
    ),
  });

  if (!canRead) {
    return (
      <MDBox>
        <NoPermissionNotice message="Your role doesn't include read access to Questionnaires. Ask an administrator to update your permissions." />
      </MDBox>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" gap={1.5}>
        <CircularProgress sx={{ color: ACCENT }} />
        <MDTypography variant="body2" color="text.secondary">Loading questionnaires…</MDTypography>
      </Box>
    );
  }

  return (
    <MDBox>
      {/* ── Header card (matches FeaturesPage) ── */}
      <Card sx={{
        mb: 2.5, px: 3, py: 1.8,
        background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
        boxShadow: '0 6px 24px rgba(102,126,234,0.4)', borderRadius: 3,
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 38, height: 38, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <QuizOutlined sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                Questionnaires
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.3, mt: 0.3 }}>
                {questionnaires.length} questionnaire{questionnaires.length !== 1 ? 's' : ''} — link questionnaires to service pages
              </MDTypography>
            </Box>
          </Box>
          {canCreate && (
            <MDButton
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateModalOpen(true)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.22)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)', boxShadow: 'none',
                fontSize: 14, fontWeight: 600, px: 2.5, py: 1, textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.32)', boxShadow: 'none' },
              }}
            >
              + Create Questionnaire
            </MDButton>
          )}
        </Box>
      </Card>

      {/* ── Content ── */}
      <Card sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <MDBox pt={1}>
          {questionnaires.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <QuizOutlined sx={{ fontSize: 52, color: 'text.disabled', mb: 1.5 }} />
              <MDTypography variant="h6" color="text.secondary" mb={1}>
                No questionnaires yet
              </MDTypography>
              <MDTypography variant="body2" color="text.disabled" mb={3}>
                Create your first questionnaire to link it to a service page.
              </MDTypography>
              {canCreate && (
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Add />}
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create First Questionnaire
                </MDButton>
              )}
            </Box>
          ) : (
            <DataTable
              table={{
                columns,
                rows: questionnaires.map((q, idx) => getRow(q, idx)),
              }}
              isSorted={false}
              entriesPerPage={{ defaultValue: 10 }}
              showTotalEntries={true}
              noEndBorder
              canSearch={true}
            />
          )}
        </MDBox>
      </Card>

      {/* ── Modals (unchanged) ── */}
      <CreateQuestionnaireModal
        open={createModalOpen || editModalOpen}
        onClose={handleCloseModals}
        questionnaire={selectedQuestionnaire}
        onSave={handleSave}
      />
      <ViewQuestionnaireModal
        open={viewModalOpen}
        onClose={handleCloseModals}
        questionnaire={selectedQuestionnaire}
      />
    </MDBox>
  );
}

export default QuestionnairesPage;
